import { Card, CardContent } from "./ui/card";
import { FileText, Download, ExternalLink, Headphones, Layers } from "lucide-react";
import { useState } from "react";

/**
 * Convert a YouTube/Vimeo URL into an embeddable URL when possible.
 * Falls back to the original URL.
 */
const toEmbedUrl = (url) => {
  if (!url) return "";
  try {
    const u = new URL(url);
    // YouTube watch?v=ID → embed/ID
    if (/youtube\.com$/i.test(u.hostname) && u.searchParams.get("v")) {
      return `https://www.youtube.com/embed/${u.searchParams.get("v")}`;
    }
    if (/youtu\.be$/i.test(u.hostname)) {
      return `https://www.youtube.com/embed${u.pathname}`;
    }
    // Vimeo vimeo.com/123 → player.vimeo.com/video/123
    if (/vimeo\.com$/i.test(u.hostname) && /^\/\d+/.test(u.pathname)) {
      return `https://player.vimeo.com/video${u.pathname}`;
    }
    return url;
  } catch {
    return url;
  }
};

const isDirectVideoFile = (url) => /\.(mp4|webm|ogv|mov)(\?.*)?$/i.test(url || "");

/**
 * LessonContent — given a lesson object (with .lesson_type + .content),
 * renders the appropriate viewer (video player, PDF iframe, image, etc.).
 * Public to all logged-in users with access to the lesson.
 */
const LessonContent = ({ lesson }) => {
  const c = lesson?.content || {};
  const type = lesson?.lesson_type;

  if (!lesson) return null;

  // VIDEO
  if (type === "video" && c.video_url) {
    if (isDirectVideoFile(c.video_url)) {
      return (
        <video controls className="w-full rounded-xl bg-black" data-testid="lesson-video-player">
          <source src={c.video_url} />
          {c.captions_url && <track kind="subtitles" src={c.captions_url} default />}
        </video>
      );
    }
    return (
      <div className="aspect-video w-full rounded-xl overflow-hidden bg-black" data-testid="lesson-video-embed">
        <iframe
          src={toEmbedUrl(c.video_url)}
          title={lesson.title}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  // EMBED (iframe URL or raw <iframe> HTML)
  if (type === "embed") {
    if (c.embed_html) {
      return (
        <div
          className="w-full rounded-xl overflow-hidden bg-slate-50 border border-slate-200"
          data-testid="lesson-embed-html"
          // Admin-controlled content, trusted.
          dangerouslySetInnerHTML={{ __html: c.embed_html }}
        />
      );
    }
    if (c.embed_url) {
      return (
        <div className="aspect-video w-full rounded-xl overflow-hidden border border-slate-200" data-testid="lesson-embed-url">
          <iframe src={c.embed_url} title={lesson.title} className="w-full h-full" allowFullScreen />
        </div>
      );
    }
  }

  // PDF
  if (type === "pdf" && c.pdf_url) {
    return (
      <div className="rounded-xl overflow-hidden border border-slate-200" data-testid="lesson-pdf">
        <div className="flex items-center justify-between bg-slate-50 px-4 py-2 text-sm">
          <span className="font-medium text-slate-700">{c.pdf_label || lesson.title}</span>
          <a href={c.pdf_url} target="_blank" rel="noopener noreferrer" className="text-[#1E3A8A] hover:underline inline-flex items-center gap-1">
            Open in new tab <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        <iframe src={c.pdf_url} title={lesson.title} className="w-full h-[70vh] bg-white" />
      </div>
    );
  }

  // LINK
  if (type === "link" && c.link_url) {
    return (
      <Card className="border border-slate-200" data-testid="lesson-link">
        <CardContent className="p-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <ExternalLink className="w-5 h-5 text-[#1E3A8A]" />
            <div>
              <p className="font-semibold text-slate-900">{c.link_title || c.link_url}</p>
              <p className="text-xs text-slate-500">{c.link_url}</p>
            </div>
          </div>
          <a href={c.link_url} target="_blank" rel="noopener noreferrer" className="bg-[#1E3A8A] text-white text-sm rounded-lg px-4 py-2 hover:bg-[#0F1B3D]">
            Open link
          </a>
        </CardContent>
      </Card>
    );
  }

  // HTML
  if (type === "html" && c.html) {
    return (
      <Card className="border border-slate-200" data-testid="lesson-html">
        <CardContent className="p-6 prose prose-slate max-w-none" dangerouslySetInnerHTML={{ __html: c.html }} />
      </Card>
    );
  }

  // IMAGE
  if (type === "image" && c.image_url) {
    return (
      <figure className="rounded-xl overflow-hidden border border-slate-200" data-testid="lesson-image">
        <img src={c.image_url} alt={c.alt || lesson.title} className="w-full h-auto block" />
        {c.caption && <figcaption className="bg-slate-50 px-4 py-2 text-sm text-slate-600">{c.caption}</figcaption>}
      </figure>
    );
  }

  // AUDIO
  if (type === "audio" && c.audio_url) {
    return (
      <Card className="border border-slate-200" data-testid="lesson-audio">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-3">
            <Headphones className="w-5 h-5 text-[#1E3A8A]" />
            <p className="font-semibold text-slate-900">{lesson.title}</p>
          </div>
          <audio controls className="w-full">
            <source src={c.audio_url} />
          </audio>
          {c.transcript_url && (
            <a href={c.transcript_url} target="_blank" rel="noopener noreferrer" className="text-sm text-[#1E3A8A] hover:underline inline-flex items-center gap-1">
              <FileText className="w-3.5 h-3.5" /> Read transcript
            </a>
          )}
        </CardContent>
      </Card>
    );
  }

  // DOWNLOAD
  if (type === "download" && c.file_url) {
    return (
      <Card className="border border-slate-200" data-testid="lesson-download">
        <CardContent className="p-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Download className="w-5 h-5 text-[#1E3A8A]" />
            <div>
              <p className="font-semibold text-slate-900">{c.file_name || lesson.title}</p>
              <p className="text-xs text-slate-500">{c.file_url}</p>
            </div>
          </div>
          <a href={c.file_url} target="_blank" rel="noopener noreferrer" download className="bg-[#FFD507] text-[#0F1B3D] text-sm font-semibold rounded-lg px-4 py-2 hover:bg-[#FFC500]">
            Download
          </a>
        </CardContent>
      </Card>
    );
  }

  // TEXT
  if (type === "text" && c.text) {
    return (
      <Card className="border border-slate-200" data-testid="lesson-text">
        <CardContent className="p-6 whitespace-pre-wrap text-slate-800 leading-relaxed">{c.text}</CardContent>
      </Card>
    );
  }

  // FLASHCARDS
  if (type === "flashcards" && Array.isArray(c.cards) && c.cards.length > 0) {
    return <Flashcards cards={c.cards} />;
  }

  // Default placeholder
  return (
    <Card className="border-dashed border-2 border-slate-200" data-testid="lesson-placeholder">
      <CardContent className="p-8 text-center text-slate-500">
        <p className="font-semibold">Content coming soon</p>
        <p className="text-sm mt-1">No content has been added for this lesson yet.</p>
      </CardContent>
    </Card>
  );
};


// Small flashcards viewer — click to flip, prev/next.
const Flashcards = ({ cards }) => {
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const card = cards[idx] || {};
  const next = () => { setFlipped(false); setIdx((i) => (i + 1) % cards.length); };
  const prev = () => { setFlipped(false); setIdx((i) => (i - 1 + cards.length) % cards.length); };
  return (
    <div className="space-y-3" data-testid="lesson-flashcards">
      <div
        onClick={() => setFlipped((f) => !f)}
        className="min-h-[220px] rounded-2xl border border-slate-200 bg-white p-8 flex flex-col items-center justify-center text-center cursor-pointer hover:border-[#FFD507]/60 transition"
        data-testid="flashcard-active"
      >
        <p className="text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-3">{flipped ? "Back" : "Front"} · click to flip</p>
        <p className="text-xl font-heading font-bold text-slate-900">{flipped ? card.back : card.front}</p>
      </div>
      <div className="flex items-center justify-between text-sm">
        <button onClick={prev} className="text-slate-500 hover:text-slate-900" data-testid="flashcard-prev">← Previous</button>
        <span className="text-slate-500 inline-flex items-center gap-1"><Layers className="w-3 h-3" /> Card {idx + 1} of {cards.length}</span>
        <button onClick={next} className="text-slate-500 hover:text-slate-900" data-testid="flashcard-next">Next →</button>
      </div>
    </div>
  );
};

export default LessonContent;
